import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Database, Link as LinkIcon, CheckCircle, ShoppingCart, User, Package, Flag, BookOpen } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

export const ShoppingCartDb = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Schema State
    const [connections, setConnections] = useState<{ source: string, target: string }[]>([]);

    useEffect(() => {
        if (errorMessage) {
            const timer = setTimeout(() => setErrorMessage(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [errorMessage]);

    const toggleConnection = (source: string, target: string) => {
        const id = `${source}-${target}`;
        const exists = connections.find(c => c.source === source && c.target === target);

        if (exists) {
            setConnections(prev => prev.filter(c => !(c.source === source && c.target === target)));
        } else {
            setConnections(prev => [...prev, { source, target }]);
        }
    };

    const checkSchema = () => {
        // Validation: Needs to connect Users->Orders and Products->Orders
        const hasUserOrder = connections.some(c =>
            (c.source === 'users' && c.target === 'orders') ||
            (c.source === 'orders' && c.target === 'users')
        );

        const hasProductOrder = connections.some(c =>
            (c.source === 'products' && c.target === 'orders') ||
            (c.source === 'orders' && c.target === 'products')
        );

        if (hasUserOrder && hasProductOrder) {
            if (!completed) {
                setCompleted(true);
                completeModule('shopping-cart-db', 100);
                setShowSuccess(true);
            }
        } else {
            setErrorMessage("Not quite! We need to know WHO bought the item, and WHAT they bought.");
        }
    };

    const lessons: LessonPage[] = [
        {
            id: 1,
            title: "Relational Power",
            icon: <Database className="w-16 h-16 text-blue-500" />,
            content: (
                <div className="space-y-6">
                    <p className="text-xl text-slate-600 leading-relaxed">
                        In a Relational Database (like Supabase/PostgreSQL), we don't stuff everything into one huge table.
                        Instead, we keep data clean by separating it into focused tables and linking them together.
                    </p>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-100">
                            <h3 className="font-bold text-blue-800 text-lg mb-2 flex items-center gap-2">
                                <CheckCircle size={20} /> Foreign Keys (FK)
                            </h3>
                            <p className="text-blue-700">
                                A special ID column that points to a specific row in another table. It acts like a digital bridge.
                            </p>
                        </div>
                        <div className="bg-green-50 p-6 rounded-2xl border-2 border-green-100">
                            <h3 className="font-bold text-green-800 text-lg mb-2 flex items-center gap-2">
                                <CheckCircle size={20} /> Normalization
                            </h3>
                            <p className="text-green-700">
                                The practice of organizing data to reduce redundancy. Store a user's address once, reference it everywhere!
                            </p>
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 2,
            title: "The Mission",
            icon: <Flag className="w-16 h-16 text-red-500" />,
            content: (
                <div className="space-y-6">
                    <p className="text-xl text-slate-600 leading-relaxed">
                        Gorilla Mart needs a backend! Your task is to link <strong>Users</strong> and <strong>Products</strong> to the central <strong>Orders</strong> table so we can track exactly <strong>Who</strong> ordered <strong>What</strong>.
                    </p>
                    <div className="bg-slate-900 text-slate-200 p-6 rounded-2xl font-mono text-sm leading-loose shadow-xl">
                        <div className="text-slate-500 mb-2 border-b border-slate-700 pb-2">// The SQL query we want to enable:</div>
                        <span className="text-purple-400">SELECT</span> * <span className="text-purple-400">FROM</span> orders <br />
                        <span className="text-blue-400">JOIN</span> users <span className="text-blue-400">ON</span> orders.user_id = users.id
                    </div>
                    <p className="text-slate-500 italic text-center">
                        Connect the tables correctly in the next screen to validate the schema.
                    </p>
                </div>
            )
        }
    ];

    return (
        <LessonWrapper
            moduleName="Shopping Cart DB"
            moduleEmoji="🛒"
            moduleColor="blue"
            completed={completed}
            lessons={lessons}
            activityLabel="Build Schema"
        >
            <div className="max-w-4xl mx-auto py-8">
                {/* Schema Builder */}
                <div className="bg-white rounded-3xl p-8 shadow-xl border-4 border-blue-100 relative min-h-[500px]">
                    <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                        <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
                            <LinkIcon size={24} />
                        </div>
                        Schema Designer
                    </h2>

                    <div className="flex justify-between items-center h-[300px]">
                        {/* Users Table */}
                        <motion.div
                            className="w-48 bg-slate-50 border-2 border-slate-200 rounded-xl p-4 shadow-sm"
                            whileHover={{ scale: 1.02 }}
                        >
                            <div className="flex items-center gap-2 font-bold text-slate-700 border-b pb-2 mb-2">
                                <User size={16} /> users
                            </div>
                            <div className="space-y-1 text-sm font-mono text-slate-500">
                                <div className="flex justify-between"><span>id</span> <span className="text-blue-500">PK</span></div>
                                <div>email</div>
                                <div>username</div>
                            </div>
                            <button
                                onClick={() => toggleConnection('users', 'orders')}
                                className={`mt-4 w-full py-2 rounded-lg text-sm font-bold transition-colors ${connections.some(c => c.source === 'users' && c.target === 'orders')
                                    ? 'bg-green-500 text-white'
                                    : 'bg-slate-200 text-slate-500 hover:bg-slate-300'
                                    }`}
                            >
                                <LinkIcon size={14} className="inline mr-1" /> Connect to Orders
                            </button>
                        </motion.div>

                        {/* Orders Table (Center) */}
                        <motion.div
                            className="w-48 bg-blue-50 border-2 border-blue-200 rounded-xl p-4 shadow-md z-10"
                            whileHover={{ scale: 1.05 }}
                        >
                            <div className="flex items-center gap-2 font-bold text-blue-800 border-b border-blue-200 pb-2 mb-2">
                                <ShoppingCart size={16} /> orders
                            </div>
                            <div className="space-y-1 text-sm font-mono text-blue-600">
                                <div className="flex justify-between"><span>id</span> <span className="text-blue-500">PK</span></div>
                                <div className="flex justify-between">
                                    <span>user_id</span>
                                    <span className={connections.some(c => c.source === 'users' && c.target === 'orders') ? "text-green-600 font-bold" : "text-slate-300"}>FK</span>
                                </div>
                                <div className="flex justify-between">
                                    <span>product_id</span>
                                    <span className={connections.some(c => c.source === 'products' && c.target === 'orders') ? "text-green-600 font-bold" : "text-slate-300"}>FK</span>
                                </div>
                                <div>status</div>
                            </div>
                        </motion.div>

                        {/* Products Table */}
                        <motion.div
                            className="w-48 bg-slate-50 border-2 border-slate-200 rounded-xl p-4 shadow-sm"
                            whileHover={{ scale: 1.02 }}
                        >
                            <div className="flex items-center gap-2 font-bold text-slate-700 border-b pb-2 mb-2">
                                <Package size={16} /> products
                            </div>
                            <div className="space-y-1 text-sm font-mono text-slate-500">
                                <div className="flex justify-between"><span>id</span> <span className="text-blue-500">PK</span></div>
                                <div>name</div>
                                <div>price</div>
                                <div>stock</div>
                            </div>
                            <button
                                onClick={() => toggleConnection('products', 'orders')}
                                className={`mt-4 w-full py-2 rounded-lg text-sm font-bold transition-colors ${connections.some(c => c.source === 'products' && c.target === 'orders')
                                    ? 'bg-green-500 text-white'
                                    : 'bg-slate-200 text-slate-500 hover:bg-slate-300'
                                    }`}
                            >
                                <LinkIcon size={14} className="inline mr-1" /> Connect to Orders
                            </button>
                        </motion.div>
                    </div>

                    {/* Visualization Lines (SVG Overlay) */}
                    <svg className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
                        {connections.map((conn, i) => (
                            <line
                                key={i}
                                x1={conn.source === 'users' ? "20%" : "80%"}
                                y1="50%"
                                x2="50%"
                                y2="50%"
                                stroke="#22c55e"
                                strokeWidth="4"
                                strokeDasharray="8"
                                className="animate-pulse"
                            />
                        ))}
                    </svg>

                    <div className="mt-8 text-center relative">
                        <AnimatePresence>
                            {errorMessage && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    className="absolute -top-16 left-0 right-0 mx-auto w-max max-w-md bg-red-100 text-red-600 px-4 py-2 rounded-xl font-bold border border-red-200 shadow-sm z-20"
                                >
                                    {errorMessage}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <button
                            onClick={checkSchema}
                            className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold text-lg hover:bg-blue-700 shadow-lg hover:shadow-blue-500/30 transition-all"
                        >
                            Validate Schema
                        </button>
                    </div>
                </div>
            </div>

            {showSuccess && (
                <ModuleSuccess
                    title="Schema Designed!"
                    onRetry={() => setShowSuccess(false)}
                >
                    <div className="space-y-4">
                        <p className="text-slate-600">
                            Excellent! You've created the backbone of the <strong>Gorilla Mart</strong> backend.
                        </p>
                        <div className="bg-blue-100 p-4 rounded-xl">
                            <h4 className="font-bold text-blue-800 text-sm mb-2">Next Steps (In Supabase):</h4>
                            <ul className="text-blue-700 text-sm list-disc pl-4 space-y-1">
                                <li>Create 'users' table</li>
                                <li>Create 'products' table</li>
                                <li>Create 'orders' with Foreign Keys</li>
                            </ul>
                        </div>
                    </div>
                </ModuleSuccess>
            )}
        </LessonWrapper>
    );
};
